import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, UserCircle } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { useLang } from "@/context/LanguageContext";
import { useClientAuth } from "@/context/ClientAuthContext";
import { brand } from "@/lib/brand";

const PRIMARY = [
  { to: "/", label: "Home", tkey: "nav.home", end: true },
  { to: "/services", label: "Services", tkey: "nav.services" },
  { to: "/start-here", label: "How It Works", tkey: "nav.howItWorks" },
  { to: "/cost-calculator", label: "Pricing & Quote", tkey: "nav.pricingQuote" },
  { to: "/faq", label: "FAQ", tkey: "nav.faq" },
  { to: "/blog", label: "Blog", tkey: "nav.blog" },
];

function Logo() {
  return (
    <Link to="/" data-testid="nav-logo" className="flex items-center gap-2.5 group shrink-0">
      <img src={brand.logoMark} alt={brand.name} className="h-9 w-auto transition-transform duration-300 group-hover:scale-105" />
      <span className="font-serif text-xl font-medium text-charcoal-800 tracking-tight hidden sm:inline">
        {brand.name}
      </span>
    </Link>
  );
}

function LangToggle({ className = "", prefix = "nav" }) {
  const { lang, setLang } = useLang();
  return (
    <div className={`flex items-center rounded-full border border-sand-300 bg-white/70 p-0.5 text-xs font-semibold ${className}`} data-testid={`${prefix}-lang-toggle`}>
      {["en", "es"].map((l) => (
        <button
          key={l}
          data-testid={`${prefix}-lang-${l}`}
          onClick={() => setLang(l)}
          className={`rounded-full px-2.5 py-1 uppercase transition-colors ${
            lang === l ? "bg-terracotta text-white" : "text-charcoal-600 hover:text-terracotta"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const { t } = useLang();
  const { client } = useClientAuth();
  const loggedIn = !!client;
  const authTo = loggedIn ? "/portal" : "/account";
  const authLabel = loggedIn ? t("common.myPortal") : t("common.clientSignIn");
  const authLabelShort = loggedIn ? t("common.myPortal") : t("common.signInShort");

  const linkClass = ({ isActive }) =>
    `text-sm font-medium whitespace-nowrap transition-colors ${
      isActive ? "text-terracotta" : "text-charcoal-600 hover:text-terracotta"
    }`;

  return (
    <header className="sticky top-0 z-50 bg-sand-100/80 backdrop-blur-xl border-b border-sand-300">
      <nav className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-12 py-3.5 flex items-center justify-between gap-4">
        <Logo />

        <div className="hidden xl:flex items-center gap-6">
          {PRIMARY.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              className={linkClass}
              data-testid={`nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
            >
              {t(l.tkey)}
            </NavLink>
          ))}
        </div>

        <div className="hidden xl:flex items-center gap-2.5">
          <Link
            to="/pets"
            data-testid="nav-pets-badge"
            className="hidden 2xl:inline-flex items-center gap-1.5 rounded-full bg-sand-200 border border-sand-300 px-3.5 py-1.5 text-xs font-semibold text-charcoal-700 hover:bg-sand-300 hover:text-terracotta transition-colors whitespace-nowrap"
          >
            We move pets <span aria-hidden="true">🐾</span>
          </Link>
          <LangToggle />
          <Link
            to={authTo}
            data-testid="nav-client-signin"
            className="inline-flex items-center gap-1.5 rounded-full border border-charcoal-300 px-4 py-2 text-sm font-medium text-charcoal-800 hover:border-terracotta hover:text-terracotta transition-colors whitespace-nowrap"
          >
            <UserCircle size={16} strokeWidth={1.5} /> {authLabel}
          </Link>
          <Link
            to="/cost-calculator"
            data-testid="nav-quote-cta"
            className="rounded-full bg-terracotta px-5 py-2 text-sm font-medium text-white hover:bg-terracotta-hover transition-colors shadow-sm whitespace-nowrap"
          >
            {t("nav.getQuote")}
          </Link>
        </div>

        <div className="xl:hidden flex items-center gap-2">
          <Link
            to={authTo}
            data-testid="nav-client-signin-mobile"
            className="inline-flex items-center gap-1 rounded-full border border-sand-300 bg-white/70 px-2.5 py-1.5 text-xs font-semibold text-charcoal-700 hover:text-terracotta transition-colors"
          >
            <UserCircle size={15} strokeWidth={1.5} /> {authLabelShort}
          </Link>
          <LangToggle prefix="mobile-nav" />
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <button data-testid="nav-mobile-trigger" className="p-2 text-charcoal-800" aria-label="Open menu">
                <Menu strokeWidth={1.5} />
              </button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-sand-100 border-sand-200 w-[300px] overflow-y-auto">
              <SheetTitle className="sr-only">Menu</SheetTitle>
              <div className="flex flex-col gap-1 mt-8">
                <Link
                  to="/pets"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-pets-badge"
                  className="mb-2 inline-flex items-center justify-center gap-1.5 rounded-full bg-sand-200 border border-sand-300 px-4 py-2.5 text-sm font-semibold text-charcoal-700"
                >
                  We move pets <span aria-hidden="true">🐾</span>
                </Link>
                {PRIMARY.map((l) => (
                  <NavLink
                    key={l.to}
                    to={l.to}
                    end={l.end}
                    onClick={() => setOpen(false)}
                    data-testid={`mobile-nav-${l.to === "/" ? "home" : l.to.slice(1)}`}
                    className="rounded-xl px-4 py-3 text-charcoal-700 hover:bg-sand-200 transition-colors font-medium"
                  >
                    {t(l.tkey)}
                  </NavLink>
                ))}
                <Link
                  to="/services#guides"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-guides"
                  className="rounded-xl px-4 py-3 text-charcoal-600 hover:bg-sand-200 transition-colors text-sm"
                >
                  {t("nav.guides")} →
                </Link>
                <Link
                  to={authTo}
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-client-signin"
                  className="mt-3 inline-flex items-center justify-center gap-2 rounded-full border border-charcoal-300 px-5 py-3 text-center font-medium text-charcoal-800 hover:border-terracotta hover:text-terracotta transition-colors"
                >
                  <UserCircle size={17} strokeWidth={1.5} /> {authLabel}
                </Link>
                <Link
                  to="/cost-calculator"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-quote"
                  className="mt-2 rounded-full bg-terracotta px-5 py-3 text-center font-medium text-white"
                >
                  {t("nav.getQuote")}
                </Link>
                <Link
                  to="/contact"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-contact"
                  className="mt-2 rounded-full border border-charcoal-300 px-5 py-3 text-center font-medium text-charcoal-800"
                >
                  {t("nav.contact")}
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
}
